import { useState } from "react";
import { MapPin, Mail, Headphones, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHead } from "@/components/Reveal";
import { SITE, REQUIREMENTS, submitEnquiry } from "@/lib/site";

const INFO = [
  { icon: MapPin, title: "Regional Office", value: SITE.address, href: null, testid: "contact-info-address" },
  { icon: Mail, title: "Email", value: SITE.email, href: `mailto:${SITE.email}`, testid: "contact-info-email" },
  { icon: Headphones, title: "Support", value: "24/7 | 365 Days", href: null, testid: "contact-info-support" },
  { icon: Phone, title: "Phone", value: SITE.phoneDisplay, href: SITE.phoneHref, testid: "contact-info-phone" },
];

const EMPTY = { name: "", company: "", email: "", phone: "", requirement: REQUIREMENTS[0], message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitEnquiry({ ...form, type: "enquiry" });
      toast.success("Enquiry submitted. The JAYTRIKA team will get back to you shortly.");
      setForm(EMPTY);
    } catch {
      toast.error("Could not submit your enquiry. Please email us at info@jaytrika.in.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-navy outline-none transition-all duration-200 placeholder:text-slate-400 focus:border-electric focus:ring-2 focus:ring-blue-500/20";

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHead
          eyebrow="Contact Us"
          title="Connect With JAYTRIKA"
          sub="Tell us about your telecom, fiber or network infrastructure requirement — our team responds around the clock."
        />

        <div className="mt-14 grid gap-10 lg:grid-cols-5">
          <div className="flex flex-col gap-4 lg:col-span-2">
            {INFO.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.07}>
                <div
                  data-testid={item.testid}
                  className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-5 transition-all duration-300 hover:border-blue-300 hover:bg-white hover:shadow-lg hover:shadow-blue-500/5"
                >
                  <span className="inline-flex shrink-0 rounded-xl bg-navy p-3 text-cyan-300">
                    <item.icon size={18} />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {item.title}
                    </p>
                    {item.href ? (
                      <a
                        href={item.href}
                        data-testid={`${item.testid}-link`}
                        className="mt-0.5 block text-sm font-semibold text-navy transition-colors hover:text-electric"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p className="mt-0.5 text-sm font-semibold text-navy">{item.value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}

            <Reveal delay={0.3} className="flex-1">
              <div className="h-full min-h-[240px] overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
                <iframe
                  data-testid="contact-map"
                  title="JAYTRIKA location — Manikonda, Hyderabad, Telangana"
                  src={SITE.mapEmbed}
                  className="h-full min-h-[240px] w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-3">
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="rounded-3xl border border-slate-200 bg-slate-50 p-7 sm:p-9"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Name *</label>
                  <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-company" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Company Name</label>
                  <input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Organization / company" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Email *</label>
                  <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Phone Number</label>
                  <input id="contact-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="+91" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-requirement" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Requirement</label>
                  <select id="contact-requirement" data-testid="contact-requirement-select" value={form.requirement} onChange={set("requirement")} className={inputCls}>
                    {REQUIREMENTS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Message</label>
                  <textarea id="contact-message" data-testid="contact-message-input" rows={5} value={form.message} onChange={set("message")} placeholder="Briefly describe your requirement — route, quantity, timeline or site details." className={`${inputCls} resize-none`} />
                </div>
              </div>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="group mt-7 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-electric px-8 py-4 text-sm font-semibold text-white shadow-xl shadow-blue-600/25 transition-all duration-300 hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {sending ? "Submitting..." : "Submit Enquiry"}
                <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
